"use client"

import { useQuery } from "@tanstack/react-query"

import { fetchFinanceHome } from "@/features/dashboard/home-d4/services/home-dashboard-api"
import { fetchBranchBoard } from "@/features/finance/overview/branch-f1/services/branch-board-api"
import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"
import { financeQueryKeys } from "@/features/finance/services/finance-query-keys"

/** The treasury tile's weekly bars: nine weeks, as F1 draws them. */
export const BRANCH_TREASURY_WEEKS = 9

type Options = { enabled: boolean; period?: { from: string; to: string } }

/**
 * The board's branch (the inspected one on Branch Details) and a period: the board's, or a fallback one. Every
 * F1 tile asks through these two hooks, so React Query makes one request per period.
 */
function useBranchPeriod(options: Options) {
  const scope = useFinanceOverviewScope()
  const boardPeriod = useFinanceOverviewPeriod()
  const period = options.period ?? boardPeriod
  const dateFrom = period.from ?? ""
  const dateTo = period.to ?? ""
  const enabled = options.enabled && Boolean(scope.workspaceId && scope.branchId && dateFrom && dateTo)
  return { workspaceId: scope.workspaceId, branchId: scope.branchId, dateFrom, dateTo, enabled }
}

/** `GET /finance/overview/branch`. A refusal (403) will be refused again, so it is not retried. */
export function useBranchBoardQuery(options: Options) {
  const query = useBranchPeriod(options)
  return useQuery({
    queryKey: financeQueryKeys.branchBoard(query),
    queryFn: () =>
      fetchBranchBoard({
        workspaceId: query.workspaceId ?? "",
        branchId: query.branchId ?? "",
        dateFrom: query.dateFrom,
        dateTo: query.dateTo,
      }),
    enabled: query.enabled,
    retry: false,
  })
}

/** `GET /finance/overview/home` with nine weeks: cash, the period's money and the weekly series. */
export function useBranchTreasuryQuery(options: Options) {
  const query = useBranchPeriod(options)
  return useQuery({
    queryKey: financeQueryKeys.branchTreasury({ ...query, weeks: BRANCH_TREASURY_WEEKS }),
    queryFn: () =>
      fetchFinanceHome({
        workspaceId: query.workspaceId ?? "",
        branchId: query.branchId ?? "",
        dateFrom: query.dateFrom,
        dateTo: query.dateTo,
        weeks: BRANCH_TREASURY_WEEKS,
      }),
    enabled: query.enabled,
    retry: false,
  })
}
